/**
 * AccountTypeSelector Component
 * Dropdown selector for account type and subtype with appropriate icons
 */
import { useMemo } from 'react';
import Select from '@/components/ui/Select';
import type { AccountType, AccountSubtype } from '@/types';

export interface AccountTypeSelectorProps {
  type: string;
  subtype: string;
  onTypeChange: (type: AccountType) => void;
  onSubtypeChange: (subtype: AccountSubtype) => void;
  className?: string;
}

// Account type configurations with labels and their subtypes
// Must match backend AccountType enum (services/core/app/models/account.py)
const ACCOUNT_TYPE_CONFIG: Record<AccountType, {
  label: string;
  subtypes: { value: AccountSubtype; label: string }[];
}> = {
  depository: {
    label: 'Bank Account',
    subtypes: [
      { value: 'checking', label: 'Checking' },
      { value: 'savings', label: 'Savings' },
      { value: 'money_market', label: 'Money Market' },
      { value: 'cd', label: 'Certificate of Deposit (CD)' },
      { value: 'cash', label: 'Cash' },
    ],
  },
  credit: {
    label: 'Credit',
    subtypes: [
      { value: 'credit_card', label: 'Credit Card' },
    ],
  },
  loan: {
    label: 'Loan',
    subtypes: [
      { value: 'mortgage', label: 'Mortgage' },
      { value: 'auto', label: 'Auto Loan' },
      { value: 'student', label: 'Student Loan' },
      { value: 'personal', label: 'Personal Loan' },
      { value: 'home_equity', label: 'Home Equity' },
    ],
  },
  investment: {
    label: 'Investment',
    subtypes: [
      { value: 'brokerage', label: 'Brokerage' },
      { value: 'ira', label: 'IRA' },
      { value: 'roth', label: 'Roth IRA' },
      { value: '401k', label: '401(k)' },
      { value: '403b', label: '403(b)' },
      // 529 is a valid AccountSubtype in the backend enum
      // (models/account.py:101) and in types/index.ts, and useFlowData already
      // categorises it (useFlowData.ts:84,155) - but it was never offered here,
      // so a college savings plan could not be added as a manual account.
      { value: '529', label: '529 College Savings' },
    ],
  },
  insurance: {
    label: 'Insurance',
    subtypes: [
      { value: 'whole_life', label: 'Whole Life' },
      { value: 'universal_life', label: 'Universal Life' },
      { value: 'annuity', label: 'Annuity' },
    ],
  },
  real_estate: {
    label: 'Real Estate',
    subtypes: [
      { value: 'primary_residence', label: 'Primary Residence' },
      { value: 'rental', label: 'Rental Property' },
      { value: 'real_estate', label: 'Other Real Estate' },
      { value: 'land', label: 'Land' },
    ],
  },
  other_asset: {
    label: 'Other Asset',
    subtypes: [
      { value: 'vehicle', label: 'Vehicle' },
      { value: 'collectible', label: 'Collectible' },
      { value: 'crypto', label: 'Cryptocurrency' },
      { value: 'crypto_wallet', label: 'Crypto Wallet' },
      { value: 'business_equity', label: 'Business Equity' },
      { value: 'other_asset', label: 'Other Asset' },
    ],
  },
  other_liability: {
    label: 'Other Liability',
    subtypes: [
      { value: 'other_liability', label: 'Other Liability' },
    ],
  },
  other: {
    label: 'Other',
    subtypes: [
      { value: 'other', label: 'Other' },
    ],
  },
};

// Build type options for the select
const TYPE_OPTIONS = Object.entries(ACCOUNT_TYPE_CONFIG).map(([value, config]) => ({
  value,
  label: config.label,
}));

export default function AccountTypeSelector({
  type,
  subtype,
  onTypeChange,
  onSubtypeChange,
  className,
}: AccountTypeSelectorProps) {
  // Get subtypes for the selected type
  const subtypeOptions = useMemo(() => {
    const config = ACCOUNT_TYPE_CONFIG[type as AccountType];
    return config?.subtypes || [];
  }, [type]);

  // Handle type change - reset subtype to first option of new type
  const handleTypeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newType = e.target.value as AccountType;
    onTypeChange(newType);

    // Set subtype to first option of new type
    const newConfig = ACCOUNT_TYPE_CONFIG[newType];
    const firstSubtype = newConfig?.subtypes[0];
    if (firstSubtype) {
      onSubtypeChange(firstSubtype.value);
    }
  };

  return (
    <div className={className}>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Select
          label="Account Type"
          value={type}
          onChange={handleTypeChange}
          options={TYPE_OPTIONS}
          aria-label="Select account type"
        />

        <Select
          label="Subtype"
          value={subtype}
          onChange={(e) => onSubtypeChange(e.target.value as AccountSubtype)}
          options={subtypeOptions}
          aria-label="Select account subtype"
        />
      </div>
    </div>
  );
}

/**
 * Get display label for a subtype value
 */
export function getSubtypeLabel(subtype: string): string {
  for (const config of Object.values(ACCOUNT_TYPE_CONFIG)) {
    const found = config.subtypes.find((s) => s.value === subtype);
    if (found) return found.label;
  }
  // Fallback: capitalize and replace underscores
  return subtype
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

/**
 * Get display label for a type value
 */
export function getTypeLabel(type: string): string {
  const config = ACCOUNT_TYPE_CONFIG[type as AccountType];
  return config?.label || type.charAt(0).toUpperCase() + type.slice(1);
}

export { ACCOUNT_TYPE_CONFIG };
